import {
  createContext,
  createEffect,
  createMemo,
  type ParentComponent,
  splitProps,
  useContext,
} from "solid-js";
import { createStore } from "solid-js/store";
import { cleanObject } from "@/libs/global";
import { ALLOWED_FILTERS, FILTERS_CONFIG } from "../constants/filters";
import type {
  AllowedFilters,
  AllowedFilterValues,
  AllowedSortFields,
  AllowedSortOrders,
} from "../types";
import { parseRenewableFromSearchParams } from "../utils/parser";

export type FilterStore = Partial<AllowedFilterValues> & {
  availableFilters: AllowedFilters[];
  sortBy: AllowedSortFields;
  sortOrder: AllowedSortOrders;
  search?: string;
  for_me?: boolean;
};

export const standardizeFilterStore = (
  unparsedFilters: Partial<AllowedFilterValues> & {
    search?: string;
    renewable?: boolean | null;
  },
) => {
  const parsedFilters: FilterStore = {
    availableFilters: [],
    sortBy: "created_at",
    sortOrder: "desc",
    search: undefined,
  };
  for (const [key, value] of Object.entries(unparsedFilters)) {
    const typedKey = key as AllowedFilters;
    if (value === null) {
      parsedFilters[typedKey] = null;
    } else if (typedKey === "renewable") {
      if (typeof value === "boolean") {
        parsedFilters[typedKey] = value;
      } else {
        parsedFilters[typedKey] = parseRenewableFromSearchParams(value);
      }
    } else if (Array.isArray(value)) {
      parsedFilters[typedKey] = value.join(",");
    } else {
      parsedFilters[typedKey] = value.toString();
    }
  }
  parsedFilters.availableFilters = ALLOWED_FILTERS.filter(
    (filter) => !parsedFilters[filter],
  );
  parsedFilters.search = unparsedFilters.search?.toString();

  return parsedFilters;
};
const createFilterStore = (initialState: FilterStore) => {
  const DEFAULT_STATE: FilterStore = {
    daily_rate_min: null,
    daily_rate_max: null,
    skills: null,
    job_category: null,
    experience_level: null,
    remote_policy: null,
    start_date: null,
    renewable: null,
    availableFilters: [],
    sortBy: "created_at",
    sortOrder: "desc",
    budget: null,
    duration: null,
    effort: null,
    payment_type: null,
    payment_status: null,
    search: undefined,
    for_me: undefined,
  };
  return createStore<FilterStore>({
    ...DEFAULT_STATE,
    ...initialState,
  });
};

const FilterContext = createContext<ReturnType<typeof createFilterStore>>();

export const FilterProvider: ParentComponent<{
  initialState: FilterStore;
}> = (props) => {
  const [filterStore, setFilterStore] = createFilterStore(props.initialState);
  createEffect(() => {
    setFilterStore(
      "availableFilters",
      ALLOWED_FILTERS.filter((filter) => !filterStore[filter]),
    );
  });
  return (
    <FilterContext.Provider value={[filterStore, setFilterStore]}>
      {props.children}
    </FilterContext.Provider>
  );
};

export const useFilterStore = () => {
  const store = useContext(FilterContext);
  if (!store) {
    throw new Error("FilterStore not found");
  }
  return store;
};

export const useFilterBadges = () => {
  const [filterStore] = useFilterStore();
  const filterBadges = createMemo(() => {
    const [_, filters] = splitProps(filterStore, [
      "availableFilters",
      "sortBy",
      "sortOrder",
      "payment_status",
      "search",
      "project_type",
      "for_me",
    ]);
    const cleanedFilters = cleanObject(filters);
    const badges = Object.entries(cleanedFilters)
      .filter(([_, value]) => value !== null)
      .map(([key, value]) => {
        const filterConfig =
          FILTERS_CONFIG[
            key as Exclude<AllowedFilters, "project_type" | "payment_type">
          ];
        return {
          label: filterConfig.label,
          value: value,
          key: key as Exclude<AllowedFilters, "payment_status">,
        };
      });
    return badges;
  });
  return filterBadges;
};
